import { useState, useEffect, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import type { IButton } from 'libs/design-system/src/types/Button/button';
import type { IBadge } from 'libs/design-system/src/types/Badge/badge';
import {
  EllipsisVertical,
  TrashCan,
} from '@unified-checkout/design-system/icons/index';
import {
  Badge,
  Breadcrumbs,
  Button,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  formatAmountWithCommas,
  FormatCurrencyForDisplay,
  Modal,
  Notification,
  SkeletonBase,
} from '@unified-checkout/design-system';
import { convertCentsToMajorUnit } from '@app/dashboard/utils/convertAmounts';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import type {
  ISingleOrderHeader,
  IOrderDelete,
  TOrderStatus,
} from '@app/dashboard/types/innerLayout/orders';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { FOUR_SECONDS } from '@app/dashboard/constants/shared';
import { formatToLocalizedDateTime } from '@app/dashboard/utils/dateFormats';
import { getOrderStatus } from '../../utils';
import { deleteOrder } from '@app/dashboard/services/inner_layout/orders/delete';
import { MODAL_STEP_STATUS } from '@app/dashboard/constants/orders';
import { StatusContent } from '@app/dashboard/components/inner_layout/StatusContent';
import { Danger, SuccessHeader } from '@unified-checkout/design-system/icons/state_icons';
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import styles from './header.module.scss';

const VISIBLE_ACTION_BTNS = 2;
const MAX_MODAL_WIDTH = '35rem';

export const Header = ({
  data: selectedOrder,
  isError,
  isLoading,
  refetchData,
}: ISingleOrderHeader) => {
  const id = selectedOrder?.id;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useLangStore();

  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isModalOpen, setIsModalOpen] = useState({
    delete: false,
  });

  const [params, setParams] = useState<IOrderDelete>();

  const [modalStep, setModalStep] = useState<{
    type: string;
    message: string;
  }>({
    type: MODAL_STEP_STATUS.START,
    message: '',
  });

  useEffect(() => {
    id &&
      setParams((prevParams) => ({
        ...prevParams,
        orderId: Number(id),
      }));
  }, [id]);

  const handleModalStatus = ({ message, type }: { message: string; type: string }) => {
    setModalStep({ type, message });

    setTimeout(() => {
      setIsModalOpen((prev) => ({ ...prev, delete: false }));
      if (type !== MODAL_STEP_STATUS.SUCCESS) setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
    }, FOUR_SECONDS);
  };

  const getBadgeProps = (status: TOrderStatus) => {
    const status_text = getOrderStatus(status);

    const isSuccess = status === 'PAID';
    const isPending = status === 'UNPAID';

    return {
      text: status_text,
      color: isSuccess ? 'green' : isPending ? 'yellow' : 'grey',
    } as IBadge;
  };

  const { amount_cents, currency, payment_status, created_at } =
    selectedOrder || {};

  const { isPending: isDeleteLoading, mutate: mutateDeleteOrder } = useMutation(
    {
      mutationKey: [QUERY_KEYS.DELETE_ORDER],
      mutationFn: () => (params ? deleteOrder(params) : Promise.resolve(null)),
      onSuccess: (response) => {
        const message = response?.data?.message;
        handleModalStatus({
          type: MODAL_STEP_STATUS.SUCCESS,
          message: message || t('innerLayout.ordersPage.singleOrdersPage.modals.delete.successMessage')
        });
      },
      onError: (err) => {
        const axiosErr = err as AxiosError;
        const response = axiosErr?.response as any;
        const status = response?.status;
        const message = response?.data?.message;

        if (response) {
          if (status === 400) {
            handleModalStatus({
              type: MODAL_STEP_STATUS.FAILED,
              message: message || t('innerLayout.ordersPage.singleOrdersPage.modals.delete.errors.trnxCantBeRefunded')
            });
          }
        } else {
          handleModalStatus({
            type: MODAL_STEP_STATUS.FAILED,
            message: t('innerLayout.errors.smthWentWrong')
          });
        }
      },
    }
  );

  const handleDelete = async () => {
    await setParams((prevParams) => {
      return {
        ...prevParams,
      } as any;
    });

    mutateDeleteOrder();
  };

  const headerActionBtnsSharedProps = {
    size: 'md' as const,
    color: 'secondary',
    variant: 'outlined',
  } as const;

  const headerActionBtns: IButton[] = [
    ...(isAccessible(PERMISSIONS.CAN_DELETE_ORDERS) ? [{
      ...headerActionBtnsSharedProps,
      text: t('innerLayout.ordersPage.singleOrdersPage.delete'),
      color: 'danger' as const,
      onClickHandler: () => setIsModalOpen({ ...isModalOpen, delete: true }),
    }] : []),
  ];

  const visibleActionBtns = headerActionBtns.slice(0, VISIBLE_ACTION_BTNS);
  const collapsedActionBtns = headerActionBtns.slice(VISIBLE_ACTION_BTNS);

  const handleModalClose = () => {
    setIsModalOpen({ ...isModalOpen, delete: false });
  };

  const handleConfirmDelete = () => {
    handleDelete();
  };

  const deletedModalBtnProps: IButton[] = [
    {
      text: t('innerLayout.ordersPage.singleOrdersPage.modals.delete.confirmButton'),
      onClickHandler: handleConfirmDelete,
      color: 'danger',
      variant: 'contained',
      loading: isDeleteLoading,
    },
    {
      text: t('innerLayout.ordersPage.singleOrdersPage.modals.delete.cancelButton'),
      onClickHandler: handleModalClose,
      color: 'secondary',
      variant: 'outlined',
    },
  ];

  const renderDeleteModalContent = () => {
    if (modalStep.type === MODAL_STEP_STATUS.START) {
      return (
        <p className={styles['header__modal-text']}>
          {t('innerLayout.ordersPage.singleOrdersPage.modals.delete.confirmMessage').replace('{{orderId}}', id)}
        </p>
      );
    } else {
      return (
        <StatusContent
          state={modalStep.type === MODAL_STEP_STATUS.SUCCESS ? 'success' : 'danger'}
          title={t(`innerLayout.ordersPage.singleOrdersPage.modals.delete.${modalStep.type}`)}
          statusIcon={modalStep.type === MODAL_STEP_STATUS.SUCCESS ? SuccessHeader : Danger
          }
        />
      );
    }
  };

  useEffect(() => {
    if (modalStep.type === MODAL_STEP_STATUS.SUCCESS && !isModalOpen.delete) {
      navigate(INNER_ROUTES.ORDERS);
    }
  }, [modalStep, isModalOpen]);

  return (
    !isError && (
      <div className={styles['header']}>
        <div className={styles['header__breadcrumbs']}>
          {isLoading ? (
            <SkeletonBase height="1.5rem" />
          ) : (
            <Breadcrumbs
              linksProps={[
                {
                  text: t('innerLayout.navbar.orders'),
                  onClickHandler: () => navigate(INNER_ROUTES.ORDERS),
                  active: true,
                },
                {
                  text: t('innerLayout.ordersPage.singleOrdersPage.orderId').replace('{{id}}', id),
                },
              ]}
            />
          )}
        </div>

        {isLoading ? (
          <SkeletonBase height="3.75rem" />
        ) : (
          <div className={styles['header__main-info-box']}>
            <div className={styles['header__main-info-box__info']}>
              <div className={styles['header__main-info-box__info__amount-status-box']}>
                <span>
                  {amount_cents && (
                    <>
                      {lang === 'en' && FormatCurrencyForDisplay(currency)}{' '}
                      {formatAmountWithCommas({
                        amount: convertCentsToMajorUnit(amount_cents, currency),
                        currency,
                      })}{' '}
                      {lang === 'ar' && FormatCurrencyForDisplay(currency)}
                    </>
                  )}
                </span>

                <Badge size="md" {...getBadgeProps(payment_status)} />
              </div>

              <div className={styles['header__main-info-box__info__date']}>
                {`${t('innerLayout.ordersPage.singleOrdersPage.created')}
                ${created_at && formatToLocalizedDateTime(created_at, lang)}`}
              </div>
            </div>

            <div className={styles['header__main-info-box__actions']}>
              {visibleActionBtns?.map((btn, index) => {
                return (
                  <Button
                    key={`single-order-header-action-btn-${index}`}
                    {...btn}
                  />
                );
              })}

              {!!collapsedActionBtns.length && (
                <Dropdown width="fit-content">
                  <DropdownButton
                    leadingIcon={EllipsisVertical}
                    hideDropdownChevron
                  />
                  <DropdownMenu width="fit-content" horizontalPlacement="end">
                    {collapsedActionBtns.map((actionBtn, index) => (
                      <DropdownMenuItem
                        text={actionBtn.text}
                        icon={actionBtn.leadingIcon as ReactElement<'svg'>}
                        onClickHandler={actionBtn.onClickHandler}
                        key={`single-trnx-header-action-btn-${index}`}
                      />
                    ))}
                  </DropdownMenu>
                </Dropdown>
              )}
            </div>
          </div>
        )}

        {
          <Modal
            isMobile={isMobile}
            lang={lang}
            open={isModalOpen.delete}
            maxWidth={MAX_MODAL_WIDTH}
            closeOnClickOutside={false}
            onCloseHandler={handleModalClose}
            headerIcon={modalStep.type === MODAL_STEP_STATUS.START ? TrashCan : undefined}
            title={modalStep.type === MODAL_STEP_STATUS.START ? t( 'innerLayout.ordersPage.singleOrdersPage.modals.delete.confirmTitle'): ''}
            btnsProps={modalStep.type === MODAL_STEP_STATUS.START ? deletedModalBtnProps : undefined}
            hasCloseIcon={modalStep.type !== MODAL_STEP_STATUS.START}
            wrapperClassName={styles['header__modal-wrapper']}
          >
            {renderDeleteModalContent()}
          </Modal>
        }
      </div>
    )
  );
};
